'use client';
import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './DemoVideoModal.css';

// ── Canvas logical resolution (16:9) ─────────────────────────────────────────
const LW = 640;
const LH = 360;
const TOTAL = 46;

// ── Easing ────────────────────────────────────────────────────────────────────
const easeOut3  = t => 1 - Math.pow(1 - t, 3);
const easeInOut = t => t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = t => Math.max(0, Math.min(1, t));

// ── Brand palette ─────────────────────────────────────────────────────────────
const P = {
  bg:     '#09090f',
  s2:     '#1a1a2e',
  brand:  '#6366f1',
  purple: '#8b5cf6',
  cyan:   '#06b6d4',
  green:  '#10b981',
  amber:  '#f59e0b',
  pink:   '#ec4899',
  text:   '#f1f5f9',
  muted:  '#64748b',
};

function rgba(hex, a) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
}

// ── Drawing primitives ────────────────────────────────────────────────────────
function txt(ctx, x, y, str, size, color, align = 'center', weight = '400') {
  ctx.save();
  ctx.font = `${weight} ${size}px 'Segoe UI', Inter, -apple-system, sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(str, x, y);
  ctx.restore();
}

function glow(ctx, x, y, r, color, intensity) {
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, rgba(color, intensity));
  g.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function circ(ctx, x, y, r, fill, stroke, strokeW = 1.5) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = strokeW; ctx.stroke(); }
}

function pill(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 1; ctx.stroke(); }
}

function dotGrid(ctx) {
  ctx.save();
  ctx.strokeStyle = 'rgba(99,102,241,0.05)';
  ctx.lineWidth = 1;
  for (let x = 0; x <= LW; x += 32) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,LH); ctx.stroke(); }
  for (let y = 0; y <= LH; y += 32) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(LW,y); ctx.stroke(); }
  ctx.restore();
}

// ── Scene alpha helper ────────────────────────────────────────────────────────
function sa(elapsed, start, end, fade = 1.5) {
  return easeInOut(Math.min(
    clamp01((elapsed - start) / fade),
    clamp01((end - elapsed) / fade),
  ));
}

// ── Gormaran logo (rounded rect + G fallback, or PNG image) ──────────────────
function drawLogoIcon(ctx, cx, cy, size, logo) {
  if (logo && logo.complete && logo.naturalWidth > 0) {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(cx - size/2, cy - size/2, size, size, size * 0.22);
    ctx.clip();
    ctx.drawImage(logo, cx - size/2, cy - size/2, size, size);
    ctx.restore();
  } else {
    ctx.save();
    const g = ctx.createLinearGradient(cx-size/2, cy-size/2, cx+size/2, cy+size/2);
    g.addColorStop(0, P.brand); g.addColorStop(1, P.purple);
    ctx.beginPath();
    ctx.roundRect(cx - size/2, cy - size/2, size, size, size * 0.22);
    ctx.fillStyle = g;
    ctx.fill();
    txt(ctx, cx, cy, 'G', size * 0.54, '#fff', 'center', '800');
    ctx.restore();
  }
}

// ── Real social channel icons ─────────────────────────────────────────────────
function drawLinkedIn(ctx, cx, cy, size) {
  ctx.save();
  const r = size * 0.2;
  ctx.fillStyle = '#0077b5';
  ctx.beginPath(); ctx.roundRect(cx-size/2, cy-size/2, size, size, r); ctx.fill();
  txt(ctx, cx, cy, 'in', size*0.42, '#fff', 'center', '800');
  ctx.restore();
}

function drawInstagram(ctx, cx, cy, size) {
  ctx.save();
  const r = size * 0.22;
  const grad = ctx.createLinearGradient(cx-size/2, cy+size/2, cx+size/2, cy-size/2);
  grad.addColorStop(0, '#f09433'); grad.addColorStop(0.25, '#e6683c');
  grad.addColorStop(0.5, '#dc2743'); grad.addColorStop(0.75, '#cc2366');
  grad.addColorStop(1, '#bc1888');
  ctx.fillStyle = grad;
  ctx.beginPath(); ctx.roundRect(cx-size/2, cy-size/2, size, size, r); ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = size*0.07; ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.roundRect(cx-size*0.28, cy-size*0.26, size*0.56, size*0.5, size*0.08); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy+size*0.01, size*0.13, 0, Math.PI*2); ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.beginPath(); ctx.arc(cx+size*0.18, cy-size*0.16, size*0.05, 0, Math.PI*2); ctx.fill();
  ctx.restore();
}

function drawEmail(ctx, cx, cy, size) {
  ctx.save();
  const r = size * 0.2;
  ctx.fillStyle = '#ea4335';
  ctx.beginPath(); ctx.roundRect(cx-size/2, cy-size/2, size, size, r); ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = size*0.08; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.roundRect(cx-size*0.3, cy-size*0.2, size*0.6, size*0.4, 2); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx-size*0.3, cy-size*0.2);
  ctx.lineTo(cx, cy+size*0.06);
  ctx.lineTo(cx+size*0.3, cy-size*0.2);
  ctx.stroke();
  ctx.restore();
}

function drawX(ctx, cx, cy, size) {
  ctx.save();
  const r = size * 0.2;
  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.roundRect(cx-size/2, cy-size/2, size, size, r); ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = size*0.12; ctx.lineCap = 'round';
  const m = size * 0.26;
  ctx.beginPath(); ctx.moveTo(cx-m, cy-m); ctx.lineTo(cx+m, cy+m); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx+m, cy-m); ctx.lineTo(cx-m, cy+m); ctx.stroke();
  ctx.restore();
}

// ── Orb data (all Spanish) ────────────────────────────────────────────────────
const ORBS = [
  { c: P.brand,  emoji: '👗', label: 'Boutique Moda',  fx: 1.3, fy: 1.7, px: 0.0, py: 0.5 },
  { c: P.cyan,   emoji: '💻', label: 'Startup Tech',    fx: 1.1, fy: 0.9, px: 1.2, py: 0.3 },
  { c: P.green,  emoji: '🍕', label: 'Restaurante',     fx: 0.7, fy: 1.4, px: 2.1, py: 1.8 },
  { c: P.amber,  emoji: '🏠', label: 'Inmobiliaria',    fx: 1.6, fy: 0.8, px: 0.7, py: 2.5 },
  { c: P.pink,   emoji: '💪', label: 'Club Deportivo',  fx: 0.9, fy: 1.1, px: 3.1, py: 1.1 },
];

function chaosPos(orb, t) {
  return {
    x: LW * (0.5 + 0.38 * Math.sin(orb.fx * t * 0.8 + orb.px)),
    y: LH * (0.5 + 0.32 * Math.sin(orb.fy * t * 0.8 + orb.py)),
  };
}

// ── SCENE 1: Problem (fast — 2.5s visible, fade=0.75) ────────────────────────
function drawProblem(ctx, g, a) {
  ctx.save();
  ctx.globalAlpha = a;
  dotGrid(ctx);
  glow(ctx, LW*0.5, LH*0.4, 200, P.brand, 0.07);

  ORBS.forEach((orb, i) => {
    const { x, y } = chaosPos(orb, g);
    glow(ctx, x, y, 50, orb.c, 0.22);
    circ(ctx, x, y, 20, orb.c);
    ctx.font = '14px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(orb.emoji, x, y);
    const angle = g * 0.7 + i * 1.26;
    const bx = x + Math.cos(angle) * 34, by = y + Math.sin(angle) * 30;
    const bA = 0.28 + 0.22 * Math.sin(g * 1.3 + i);
    ctx.globalAlpha = a * bA;
    const tasks = ['Email','Post','Informe','Análisis','Propuesta'];
    pill(ctx, bx-20, by-7, 40, 14, 4, rgba(P.brand, 0.14), rgba(P.brand, 0.28));
    txt(ctx, bx, by, tasks[i], 7.5, rgba(P.brand, 0.85));
    ctx.globalAlpha = a;
    txt(ctx, x, y + 30, orb.label, 9.5, P.muted);
  });

  const tA = easeOut3(clamp01((g - 1) / 1));
  ctx.globalAlpha = a * tA;
  txt(ctx, LW/2, LH*0.87, 'Múltiples clientes. Tareas que nunca terminan.', 17, P.text, 'center', '700');
  txt(ctx, LW/2, LH*0.94, '¿Y si pudiera gestionarlo todo desde un solo lugar?', 11.5, P.muted);
  ctx.globalAlpha = a;
  ctx.restore();
}

// ── SCENE 2: Hub ──────────────────────────────────────────────────────────────
function drawHub(ctx, loc, g, a, logo) {
  ctx.save();
  ctx.globalAlpha = a;
  dotGrid(ctx);

  const CX = LW/2, CY = LH/2 + 8, R = 118;
  const pulse = 0.3 + 0.08 * Math.sin(loc * 2);
  glow(ctx, CX, CY, 90, P.brand, pulse);
  glow(ctx, CX, CY, 45, P.purple, 0.32);

  const hubA = easeOut3(clamp01(loc / 1.2));
  ctx.globalAlpha = a * hubA;
  drawLogoIcon(ctx, CX, CY, 52, logo);
  txt(ctx, CX, CY + 38, 'Gormaran.io', 10, rgba(P.brand, 0.7));
  ctx.globalAlpha = a;

  ORBS.forEach((orb, i) => {
    const settleT = easeInOut(clamp01(loc / 3));
    const angle = (i / ORBS.length) * Math.PI * 2 - Math.PI/2 + loc * 0.18;
    const orbitX = CX + Math.cos(angle) * R;
    const orbitY = CY + Math.sin(angle) * R;
    const sceneStart = g - loc;
    const { x: cx0, y: cy0 } = chaosPos(orb, sceneStart);
    const x = lerp(cx0, orbitX, settleT);
    const y = lerp(cy0, orbitY, settleT);

    const lineP = easeOut3(clamp01((loc - 1 - i * 0.25) / 1.5));
    if (lineP > 0) {
      ctx.save();
      ctx.setLineDash([4, 7]);
      ctx.strokeStyle = rgba(orb.c, 0.2);
      ctx.lineWidth = 1;
      ctx.beginPath();
      const fx = CX + Math.cos(angle) * 28, fy = CY + Math.sin(angle) * 28;
      ctx.moveTo(fx, fy);
      ctx.lineTo(fx + (x-fx)*lineP, fy + (y-fy)*lineP);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    glow(ctx, x, y, 36, orb.c, 0.2);
    circ(ctx, x, y, 18, orb.c);
    ctx.font = '12px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(orb.emoji, x, y);
    txt(ctx, x, y+27, orb.label, 9, P.muted);
  });

  ctx.globalAlpha = a * easeOut3(clamp01((loc-2)/2));
  txt(ctx, LW/2, LH*0.09, 'Un solo hub para todos tus clientes', 20, P.text, 'center', '700');
  txt(ctx, LW/2, LH*0.17, 'Organiza, delega y genera contenido desde un lugar.', 12, P.muted);
  ctx.globalAlpha = a;
  ctx.restore();
}

// ── SCENE 3: Dashboard mock ───────────────────────────────────────────────────
function drawDashboard(ctx, loc, a, logo) {
  ctx.save();
  ctx.globalAlpha = a;
  dotGrid(ctx);

  // Window dimensions
  const WX = 35, WY = 58, WW = 570, WH = 252;
  const CHROME = 22, NAV = 28, SIDE = 112;
  const CX = WX + SIDE, CY = WY + CHROME + NAV;
  const CW = WW - SIDE, CH = WH - CHROME - NAV;

  const winA = easeOut3(clamp01(loc / 0.9));
  ctx.globalAlpha = a * winA;

  // Window shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 32;
  ctx.shadowOffsetY = 12;
  pill(ctx, WX, WY, WW, WH, 12, '#0d0d1a');
  ctx.restore();

  // Window border
  ctx.beginPath(); ctx.roundRect(WX, WY, WW, WH, 12);
  ctx.strokeStyle = rgba(P.brand, 0.2); ctx.lineWidth = 1; ctx.stroke();

  // Chrome bar
  pill(ctx, WX, WY, WW, CHROME, [12,12,0,0], '#1e1e35');
  [[`#ff5f57`,WX+14],[`#febc2e`,WX+27],[`#28c840`,WX+40]].forEach(([c,x]) => {
    circ(ctx, x, WY+CHROME/2, 4.5, c);
  });
  txt(ctx, WX+WW/2, WY+CHROME/2, 'gormaran.io/dashboard', 8, 'rgba(255,255,255,0.25)');

  // App navbar
  pill(ctx, WX, WY+CHROME, WW, NAV, 0, '#12121e');
  // Logo in navbar
  drawLogoIcon(ctx, WX+18, WY+CHROME+NAV/2, 18, logo);
  txt(ctx, WX+32, WY+CHROME+NAV/2, 'Gormaran.io', 9.5, P.text, 'left', '600');
  txt(ctx, WX+WW-16, WY+CHROME+NAV/2, '⚙', 10, P.muted, 'right');

  ctx.globalAlpha = a;

  // Sidebar bg
  pill(ctx, WX, CY, SIDE, CH, [0,0,0,12], '#12121e');
  ctx.save(); ctx.beginPath(); ctx.rect(WX, CY, SIDE, CH); ctx.clip();

  const sideA = easeOut3(clamp01((loc-0.3)/0.8));
  ctx.globalAlpha = a * sideA;
  txt(ctx, WX+SIDE/2, CY+14, 'MARCAS', 7, P.muted, 'center', '700');

  const CLIENTS_D = [
    { c: P.brand, name: 'Boutique Moda'  },
    { c: P.cyan,  name: 'Startup Tech'   },
    { c: P.green, name: 'Restaurante'    },
    { c: P.amber, name: 'Inmobiliaria'   },
    { c: P.pink,  name: 'Club Deportivo' },
  ];
  CLIENTS_D.forEach((cl, i) => {
    const clA = easeOut3(clamp01((loc - 0.5 - i*0.15) / 0.5));
    ctx.globalAlpha = a * clA;
    const ey = CY + 28 + i * 35;
    if (i === 0) pill(ctx, WX+4, ey-7, SIDE-8, 22, 4, rgba(P.brand, 0.15));
    circ(ctx, WX+13, ey+4, 4.5, cl.c);
    txt(ctx, WX+22, ey+4, cl.name, 8, i===0 ? P.text : P.muted, 'left', i===0?'600':'400');
  });

  ctx.globalAlpha = a * sideA;
  txt(ctx, WX+SIDE/2, CY+CH-12, '+ Nueva marca', 7.5, rgba(P.brand, 0.65));
  ctx.restore();

  // Content area: Bandeja header
  const hdrA = easeOut3(clamp01((loc-0.7)/0.8));
  ctx.globalAlpha = a * hdrA;
  txt(ctx, CX+10, CY+16, '📥  Bandeja de contenido', 10.5, P.text, 'left', '700');
  pill(ctx, WX+WW-68, CY+7, 56, 20, 5, rgba(P.brand, 0.2), rgba(P.brand, 0.4));
  txt(ctx, WX+WW-40, CY+17, '+ Nuevo', 8, P.brand);

  ctx.globalAlpha = a;

  // Draft cards
  const CARDS = [
    { draw: drawInstagram, c:'#E1306C', client:'Boutique Moda',  type:'Post Instagram',  status:'Listo',         sc: P.green },
    { draw: (ctx,x,y,sz) => { circ(ctx,x,y,sz,'#8b5cf6'); txt(ctx,x,y,'🎬',sz*1.1,'#fff'); },
                            client:'Startup Tech',   type:'Reel · Higgsfield', status:'En revisión',   sc: P.amber },
    { draw: drawEmail,     c:'#ea4335', client:'Restaurante',    type:'Newsletter',      status:'Aprobado',      sc: P.green },
    { draw: drawLinkedIn,  c:'#0077b5', client:'Inmobiliaria',   type:'Informe mensual', status:'Generando...',  sc: P.brand },
  ];

  CARDS.forEach((card, i) => {
    const cardDelay = 1.2 + i * 0.45;
    const cardA = easeOut3(clamp01((loc - cardDelay) / 0.5));
    if (cardA <= 0) return;
    const slideX = lerp(25, 0, easeOut3(clamp01((loc-cardDelay)/0.4)));
    const cardY = CY + 32 + i * 42;

    ctx.save();
    ctx.translate(slideX, 0);
    ctx.globalAlpha = a * cardA;

    pill(ctx, CX+6, cardY, CW-12, 34, 5,
      i===0 ? rgba(P.brand, 0.08) : 'rgba(255,255,255,0.02)',
      'rgba(255,255,255,0.06)');

    // Channel icon (18x18)
    if (card.draw === drawInstagram) drawInstagram(ctx, CX+22, cardY+17, 18);
    else if (card.draw === drawEmail) drawEmail(ctx, CX+22, cardY+17, 18);
    else if (card.draw === drawLinkedIn) drawLinkedIn(ctx, CX+22, cardY+17, 18);
    else {
      circ(ctx, CX+22, cardY+17, 9, P.purple);
      ctx.font = '9px serif'; ctx.textAlign='center'; ctx.textBaseline='middle';
      ctx.fillText('🎬', CX+22, cardY+17);
    }

    txt(ctx, CX+38, cardY+12, card.client, 8.5, P.text, 'left', '600');
    txt(ctx, CX+38, cardY+24, card.type, 8, P.muted, 'left');

    const sw = 68;
    pill(ctx, CX+CW-sw-20, cardY+10, sw, 16, 8, rgba(card.sc, 0.15));
    txt(ctx, CX+CW-sw/2-20, cardY+18, card.status, 7.5, card.sc);
    txt(ctx, CX+CW-8, cardY+17, '›', 11, P.muted, 'right');
    ctx.restore();
  });

  // Top text
  ctx.globalAlpha = a * easeOut3(clamp01((loc-0.5)/1.5));
  txt(ctx, LW/2, LH*0.09, 'Tu agencia, organizada desde un solo panel.', 19, P.text, 'center', '700');
  ctx.globalAlpha = a;

  ctx.restore();
}

// ── SCENE 4: WhatsApp + Aran ──────────────────────────────────────────────────
function drawWhatsApp(ctx, loc, a) {
  ctx.save();
  ctx.globalAlpha = a;
  dotGrid(ctx);

  const PX = LW*0.03, PY = LH*0.09, PW = LW*0.47, PH = LH*0.78, HDR = 32;
  pill(ctx, PX, PY, PW, PH, 12, '#1a1a2e', rgba(P.brand, 0.12));
  pill(ctx, PX, PY, PW, HDR, [12,12,0,0], '#075e54');
  circ(ctx, PX+16, PY+HDR/2, 10, '#25d366');
  txt(ctx, PX+34, PY+HDR/2, 'Aran · Gormaran.io', 9.5, '#ecfdf5', 'left', '600');

  const MSGS = [
    { u:true,  body:'Aran, crea el post semanal para Boutique Moda', t:1.2 },
    { u:false, body:'⏳ Generando contenido...', t:2.5 },
    { u:false, body:'✅ Post listo · Caption + hashtags incluidos', t:4.2, ok:true },
    { u:true,  body:'Publícalo en Instagram y LinkedIn', t:5.5 },
    { u:false, body:'✓ Publicado en ambos canales', t:7.0, ok:true },
  ];
  MSGS.forEach((m, i) => {
    const mA = easeOut3(clamp01((loc - m.t) / 0.6));
    if (mA <= 0) return;
    const bX = PX+6, bY = PY+HDR+6+i*43, bW = PW-12;
    ctx.globalAlpha = a * mA;
    pill(ctx, bX, bY, bW, 32, 7,
      m.u ? rgba(P.brand, 0.22) : (m.ok ? rgba(P.green, 0.15) : 'rgba(255,255,255,0.05)'));
    txt(ctx, bX+9, bY+16, m.body, 9.5, m.u ? '#a5b4fc' : (m.ok ? '#34d399' : P.text), 'left');
    ctx.globalAlpha = a;
  });

  // Right: generated content card
  const CX = LW*0.54, CY = LH*0.09, CW = LW*0.43, CH = LH*0.78;
  const cardA = easeOut3(clamp01((loc-4)/1.5));
  ctx.globalAlpha = a * cardA;
  pill(ctx, CX, CY, CW, CH, 12, '#1a1a2e', rgba(P.brand, 0.25));
  pill(ctx, CX+8, CY+8, CW-16, 22, 6, rgba(P.green, 0.12));
  txt(ctx, CX+CW/2, CY+19, '✓ Borrador generado', 9, '#34d399');
  circ(ctx, CX+18, CY+48, 11, P.pink);
  txt(ctx, CX+34, CY+48, 'Boutique Moda', 10, P.text, 'left', '600');
  pill(ctx, CX+8, CY+62, CW-16, 82, 6, rgba(P.brand, 0.08));
  ctx.font = '30px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('👗', CX+CW/2, CY+103);
  ['Nueva colección primavera — descubre', 'tu estilo. ¡Disponible ahora!', '#BoutiqueModa #Moda2026'].forEach((l,i) => {
    txt(ctx, CX+9, CY+153+i*14, l, 8.5, i===2 ? rgba(P.brand, 0.8) : P.text, 'left');
  });
  [{l:'📸 IG', c:'#E1306C'}, {l:'in LI', c:'#0077b5'}].forEach((ch,i) => {
    pill(ctx, CX+8+i*56, CY+CH-32, 48, 20, 6, rgba(ch.c, 0.15));
    txt(ctx, CX+8+i*56+24, CY+CH-22, ch.l, 8, ch.c);
  });
  ctx.globalAlpha = a;

  ctx.globalAlpha = a * easeOut3(clamp01((loc-5)/2));
  txt(ctx, LW/2, LH*0.92, 'Gestiona desde WhatsApp. Sin abrir ninguna app.', 15, P.text, 'center', '700');
  ctx.globalAlpha = a;
  ctx.restore();
}

// ── SCENE 5: Output + 70% ────────────────────────────────────────────────────
const CHANNELS = [
  { angle: -Math.PI/2, draw: drawLinkedIn,  label: 'LinkedIn'   },
  { angle: 0,          draw: drawInstagram, label: 'Instagram'  },
  { angle: Math.PI/2,  draw: drawEmail,     label: 'Email'      },
  { angle: Math.PI,    draw: drawX,         label: 'X'          },
];

function drawOutput(ctx, loc, a) {
  ctx.save();
  ctx.globalAlpha = a;
  dotGrid(ctx);

  const CX = LW*0.38, CY = LH/2-10, R = 112;
  const pulse = 0.35 + 0.07 * Math.sin(loc * 2.5);
  glow(ctx, CX, CY, 80, P.brand, pulse);
  glow(ctx, CX, CY, 40, P.purple, 0.3);
  const hg = ctx.createLinearGradient(CX-24, CY-24, CX+24, CY+24);
  hg.addColorStop(0, P.brand); hg.addColorStop(1, P.purple);
  circ(ctx, CX, CY, 24, hg);
  txt(ctx, CX, CY, 'G', 19, '#fff', 'center', '700');

  CHANNELS.forEach((ch, i) => {
    const delay = i * 0.5;
    const chA = easeOut3(clamp01((loc-delay)/1));
    if (chA <= 0) return;
    const chx = CX + Math.cos(ch.angle)*R, chy = CY + Math.sin(ch.angle)*R;

    ctx.save();
    ctx.setLineDash([4,7]);
    ctx.strokeStyle = rgba(P.muted, 0.2*chA);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const ux = Math.cos(ch.angle), uy = Math.sin(ch.angle);
    ctx.moveTo(CX+ux*26, CY+uy*26);
    const len = R-46;
    ctx.lineTo(CX+ux*26+ux*len*chA, CY+uy*26+uy*len*chA);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    ctx.globalAlpha = a * chA;
    ch.draw(ctx, chx, chy, 36);
    txt(ctx, chx, chy+26, ch.label, 9, P.muted);
    ctx.globalAlpha = a;

    // Flying content card
    const cp = clamp01((loc-delay-0.5)/1.8);
    if (cp > 0 && cp < 1) {
      const fp = easeInOut(cp);
      const fx = lerp(CX, chx, fp), fy = lerp(CY, chy, fp);
      const cfa = cp < 0.7 ? 1 : (1-cp)/0.3;
      ctx.globalAlpha = a * cfa;
      pill(ctx, fx-14, fy-9, 28, 18, 5, rgba(P.brand, 0.85));
      txt(ctx, fx, fy, '✓', 10, '#fff');
      ctx.globalAlpha = a;
    }
  });

  // Counter card
  const counterA = easeOut3(clamp01((loc-2)/2));
  const pct = Math.round(easeOut3(clamp01((loc-2)/6)) * 70);
  ctx.globalAlpha = a * counterA;
  const KX = LW*0.63, KY = LH*0.26, KW = LW*0.34, KH = LH*0.44;
  pill(ctx, KX, KY, KW, KH, 14, '#1a1a2e', rgba(P.green, 0.28));
  glow(ctx, KX+KW/2, KY+KH/2, 60, P.green, 0.1);
  txt(ctx, KX+KW/2, KY+KH*0.38, `${pct}%`, 44, P.green, 'center', '800');
  txt(ctx, KX+KW/2, KY+KH*0.62, 'trabajo repetitivo', 9.5, P.muted);
  txt(ctx, KX+KW/2, KY+KH*0.73, 'automatizado', 9.5, P.muted);
  ctx.globalAlpha = a;

  ctx.globalAlpha = a * easeOut3(clamp01((loc-1)/2));
  txt(ctx, LW/2, LH*0.08, 'Contenido generado. Canales publicados.', 19, P.text, 'center', '700');
  ctx.globalAlpha = a;
  ctx.restore();
}

// ── SCENE 6: CTA ─────────────────────────────────────────────────────────────
function drawCTA(ctx, loc, a, logo) {
  ctx.save();
  ctx.globalAlpha = a;
  dotGrid(ctx);

  const CX = LW/2, CY = LH/2;
  glow(ctx, CX, CY, 220, P.purple, 0.12+0.04*Math.sin(loc*1.2));
  glow(ctx, CX, CY, 100, P.brand, 0.2);

  ctx.globalAlpha = a * easeOut3(clamp01(loc/1.2));
  drawLogoIcon(ctx, CX, CY - 58, 52, logo);

  ctx.globalAlpha = a * easeOut3(clamp01((loc-0.6)/1));
  txt(ctx, CX, CY - 14, 'Gormaran.io', 27, P.text, 'center', '700');
  txt(ctx, CX, CY + 12, 'Para agencias, consultoras y profesionales', 11, P.muted);
  txt(ctx, CX, CY + 26, 'que gestionan múltiples marcas de clientes.', 11, P.muted);

  ctx.globalAlpha = a * easeOut3(clamp01((loc-1.2)/1));
  // More generous horizontal padding on this pill
  const pillW = 200, pillH = 28;
  pill(ctx, CX - pillW/2, CY + 44, pillW, pillH, 14, rgba(P.cyan, 0.1), rgba(P.cyan, 0.35));
  txt(ctx, CX, CY + 58, 'Desde €15/mes · Sin compromiso', 9.5, P.cyan);

  ctx.globalAlpha = a * easeOut3(clamp01((loc-2)/1.2));
  const BX = CX-92, BY = CY + 85;
  const pulse = 1+0.04*Math.sin(loc*2.5);
  glow(ctx, CX, BY+19, 80*pulse, P.brand, 0.28);
  const bg = ctx.createLinearGradient(BX, BY, BX+184, BY);
  bg.addColorStop(0, P.brand); bg.addColorStop(1, P.purple);
  pill(ctx, BX, BY, 184, 38, 10, bg);
  txt(ctx, CX, BY+19, 'Empieza gratis →', 13.5, '#fff', 'center', '700');

  ctx.globalAlpha = a;
  ctx.restore();
}

// ── Scene timeline ────────────────────────────────────────────────────────────
const TIMELINE = [
  { fn: (ctx,l,g,a,logo) => drawProblem(ctx, g, a),          s:0,  e:4,  fade:0.75 },
  { fn: (ctx,l,g,a,logo) => drawHub(ctx, l, g, a, logo),     s:3,  e:12, fade:1.5  },
  { fn: (ctx,l,g,a,logo) => drawDashboard(ctx, l, a, logo),  s:11, e:22, fade:1.5  },
  { fn: (ctx,l,g,a,logo) => drawWhatsApp(ctx, l, a),         s:21, e:32, fade:1.5  },
  { fn: (ctx,l,g,a,logo) => drawOutput(ctx, l, a),           s:31, e:41, fade:1.5  },
  { fn: (ctx,l,g,a,logo) => drawCTA(ctx, l, a, logo),        s:39, e:TOTAL, fade:1.5 },
];

function renderFrame(ctx, elapsed, logo) {
  ctx.fillStyle = P.bg;
  ctx.fillRect(0, 0, LW, LH);

  TIMELINE.forEach(({ fn, s, e, fade }) => {
    const a = sa(elapsed, s, e, fade);
    if (a > 0.003) fn(ctx, elapsed-s, elapsed, a, logo);
  });

  // Progress bar
  const prog = clamp01(elapsed / TOTAL);
  ctx.fillStyle = 'rgba(255,255,255,0.07)';
  ctx.fillRect(0, LH-3, LW, 3);
  const pg = ctx.createLinearGradient(0, 0, LW, 0);
  pg.addColorStop(0, P.brand); pg.addColorStop(1, P.purple);
  ctx.fillStyle = pg;
  ctx.fillRect(0, LH-3, LW*prog, 3);
}

// ── React Component ───────────────────────────────────────────────────────────
export default function DemoVideoModal({ isOpen, onClose }) {
  const canvasRef  = useRef(null);
  const rafRef     = useRef(null);
  const animRef    = useRef({ start: 0, pauseOffset: 0, pausedAt: null });
  const tickRef    = useRef(null);
  const logoRef    = useRef(null);
  const [done, setDone]       = useState(false);
  const [playing, setPlaying] = useState(true);

  // Preload Gormaran logo PNG
  useEffect(() => {
    const img = new Image();
    img.onload = () => { logoRef.current = img; };
    img.onerror = () => { logoRef.current = null; };
    img.src = '/TP-favicon-gormaran-SaaS.png';
  }, []);

  const startAnimation = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== LW * dpr) {
      canvas.width  = LW * dpr;
      canvas.height = LH * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
    }

    const ctx = canvas.getContext('2d');
    setDone(false);
    setPlaying(true);
    animRef.current = { start: performance.now(), pauseOffset: 0, pausedAt: null };

    const tick = now => {
      const { start, pauseOffset, pausedAt } = animRef.current;
      if (pausedAt !== null) { rafRef.current = requestAnimationFrame(tick); return; }
      const elapsed = (now - start - pauseOffset) / 1000;
      renderFrame(ctx, elapsed, logoRef.current);
      if (elapsed >= TOTAL) { setDone(true); return; }
      rafRef.current = requestAnimationFrame(tick);
    };

    tickRef.current = tick;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    const id = setTimeout(startAnimation, 60);
    return () => {
      clearTimeout(id);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, startAnimation]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const togglePlay = () => {
    if (playing) {
      animRef.current.pausedAt = performance.now();
    } else {
      animRef.current.pauseOffset += performance.now() - animRef.current.pausedAt;
      animRef.current.pausedAt = null;
      if (tickRef.current) rafRef.current = requestAnimationFrame(tickRef.current);
    }
    setPlaying(p => !p);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="demo-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
        >
          <motion.div
            className="demo-modal__box"
            initial={{ opacity: 0, scale: 0.88, y: 28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 14 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={e => e.stopPropagation()}
          >
            <div className="demo-modal__header">
              <span className="demo-modal__title">Cómo funciona Gormaran.io</span>
              <button className="demo-modal__close" onClick={onClose} aria-label="Cerrar">✕</button>
            </div>

            <div className="demo-modal__canvas-wrap">
              <canvas ref={canvasRef} className="demo-modal__canvas" />
              {done && (
                <div className="demo-modal__end">
                  <button className="demo-modal__replay" onClick={startAnimation}>↺ Ver de nuevo</button>
                </div>
              )}
            </div>

            <div className="demo-modal__controls">
              <button className="demo-modal__ctrl" onClick={togglePlay}>
                {playing ? '⏸ Pausar' : '▶ Continuar'}
              </button>
              <button className="demo-modal__ctrl" onClick={startAnimation}>↺ Reiniciar</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
